import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Send, Sparkles, Loader2, Mic, Square, Wand2 } from 'lucide-react';
import { useVoiceInput } from '../../../hooks/forms/useVoiceInput';
import { useTextPolish } from '../../../hooks/forms/useTextPolish';
import { getSmsDraft, setSmsDraftText } from './draftStore';

export interface ComposeBarProps {
  /**
   * Called when user sends a message. Resolves true once the send is CONFIRMED, false if it failed: the typed text
   * stays in the box until then, so a failed send never costs the person their message.
   */
  onSend: (message: string) => Promise<boolean>;
  /** Whether a send is in progress */
  isSending: boolean;
  /** Whether the compose bar is disabled (e.g., no from/to selected) */
  disabled?: boolean;
  /** Compact styling */
  compact?: boolean;
  /**
   * Host already supplies the card; use its 16px horizontal gutter so this band
   * lines up with the thread and the From row.
   */
  flush?: boolean;
  /** Optional callback for AI Draft button */
  onAiDraft?: (currentText: string) => void;
  /**
   * Stable key identifying this conversation (e.g. normalized phone + from
   * number). When provided, the typed body is kept in memory so it survives
   * navigating away and back.
   */
  draftKey?: string;
}

/**
 * ComposeBar - Textarea with send button, AI transcribe/polish, and AI Draft.
 *
 * Features:
 * - Larger, auto-growing multi-line textarea
 * - Ctrl+Enter (or Cmd+Enter) to send
 * - AI transcribe (voice -> text) and AI polish (clean up the draft)
 * - In-memory draft persistence keyed by `draftKey`
 */
export function ComposeBar({
  onSend,
  isSending,
  disabled = false,
  compact = false,
  flush = false,
  onAiDraft,
  draftKey = '',
}: ComposeBarProps) {
  const [text, setText] = useState(() => getSmsDraft(draftKey)?.text ?? '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const maxHeight = compact ? 120 : 200;

  const resize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  }, [maxHeight]);

  // Load the stored draft when the active conversation changes, and keep the
  // textarea sized to its contents.
  useEffect(() => {
    setText(getSmsDraft(draftKey)?.text ?? '');
  }, [draftKey]);

  useEffect(() => {
    resize();
  }, [text, resize]);

  /** Update the body, persist the draft, and resize. */
  const updateText = useCallback(
    (next: string) => {
      setText(next);
      setSmsDraftText(draftKey, next);
    },
    [draftKey]
  );

  // Set the instant Send is pressed, before React re-renders `isSending`, so a double press cannot text twice.
  const sendInFlight = useRef(false);
  // The text and conversation as they are NOW, so a confirmed send clears the box only if nothing else has been typed
  // (or the person has not moved to another conversation) while it was in flight.
  const latest = useRef({ text, draftKey });
  latest.current = { text, draftKey };

  const handleSend = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed || isSending || disabled || sendInFlight.current) return;

    const sentFrom = { text, draftKey };
    sendInFlight.current = true;
    let confirmed = false;
    try {
      confirmed = await onSend(trimmed);
    } catch {
      confirmed = false; // the caller shows its own message; the text stays
    } finally {
      sendInFlight.current = false;
    }
    if (!confirmed) return;
    if (latest.current.draftKey === sentFrom.draftKey && latest.current.text === sentFrom.text) {
      updateText('');
    } else {
      // Something else was typed or another conversation is on screen: only drop the stored draft that was sent.
      if (getSmsDraft(sentFrom.draftKey)?.text === sentFrom.text) setSmsDraftText(sentFrom.draftKey, '');
    }
  }, [text, draftKey, isSending, disabled, onSend, updateText]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        void handleSend();
      }
    },
    [handleSend]
  );

  const handleInput = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      updateText(e.target.value);
    },
    [updateText]
  );

  // AI transcribe (voice -> text): append the transcription to the draft.
  const voice = useVoiceInput({
    fieldContext: 'SMS text message to a customer',
    onTranscription: (transcript) => {
      updateText(text ? `${text.trim()} ${transcript}` : transcript);
    },
  });

  // AI polish: rewrite the draft to be clean and professional.
  const polish = useTextPolish({
    fieldContext: 'SMS text message to a customer',
    customPrompt:
      'Keep it concise and friendly for a text message. Do not add a greeting or signature unless present. Preserve any links, phone numbers, dates, and times exactly.',
    tone: 'professional',
    onSuccess: (polished) => updateText(polished),
  });

  const isRecording = voice.state === 'recording';
  const isTranscribing = voice.state === 'transcribing';
  const isPolishing = polish.state === 'processing';
  const aiBusy = isTranscribing || isPolishing;

  const canSend = text.trim().length > 0 && !isSending && !disabled && !aiBusy;
  const iconBtn = compact ? 'min-h-[32px] min-w-[32px]' : 'min-h-[44px] min-w-[44px]';
  const iconSize = compact ? 'w-4 h-4' : 'w-5 h-5';

  return (
    <div
      className={`border-t border-slate-200 bg-white ${
        flush ? 'px-4 py-3' : compact ? 'p-2' : 'p-3'
      }`}
    >
      <div className="flex items-end gap-2">
        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          disabled={disabled || isSending}
          placeholder={
            disabled
              ? 'Select a line and recipient to compose...'
              : 'Type a message... (Ctrl+Enter to send)'
          }
          rows={compact ? 2 : 3}
          className={`
            flex-1 border border-slate-300 rounded-xl shadow-sm bg-white resize-none
            transition-colors duration-200 leading-relaxed
            focus:border-navy-500 focus:ring-1 focus:ring-navy-200
            disabled:bg-slate-100 disabled:cursor-not-allowed
            ${compact ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-sm'}
          `}
        />

        <div className="flex items-end gap-1.5">
          {/* AI transcribe (voice -> text) */}
          {voice.isSupported && (
            <button
              type="button"
              onClick={isRecording ? voice.stopRecording : voice.startRecording}
              disabled={disabled || isSending || isPolishing}
              title={isRecording ? 'Stop recording' : 'Dictate (AI transcribe)'}
              className={`
                flex items-center justify-center rounded-lg border transition-colors duration-200
                disabled:opacity-50 disabled:cursor-not-allowed ${iconBtn}
                ${isRecording
                  ? 'border-red-300 bg-red-50 text-red-600 animate-pulse'
                  : 'border-slate-300 text-slate-500 hover:text-navy-600 hover:border-navy-300'}
              `}
            >
              {isTranscribing ? (
                <Loader2 className={`animate-spin ${iconSize}`} />
              ) : isRecording ? (
                <Square className={iconSize} />
              ) : (
                <Mic className={iconSize} />
              )}
            </button>
          )}

          {/* AI polish */}
          <button
            type="button"
            onClick={() => polish.polishText(text)}
            disabled={disabled || isSending || text.trim().length === 0 || aiBusy}
            title="AI polish — clean up this message"
            className={`
              flex items-center justify-center rounded-lg border border-slate-300
              text-slate-500 hover:text-navy-600 hover:border-navy-300
              transition-colors duration-200
              disabled:opacity-50 disabled:cursor-not-allowed ${iconBtn}
            `}
          >
            {isPolishing ? (
              <Loader2 className={`animate-spin ${iconSize}`} />
            ) : (
              <Wand2 className={iconSize} />
            )}
          </button>

          {/* AI Draft button (optional) */}
          {onAiDraft && (
            <button
              type="button"
              onClick={() => onAiDraft(text)}
              disabled={disabled}
              title="AI Draft"
              className={`
                flex items-center justify-center rounded-lg border border-slate-300
                text-slate-500 hover:text-navy-600 hover:border-navy-300
                transition-colors duration-200
                disabled:opacity-50 disabled:cursor-not-allowed ${iconBtn}
              `}
            >
              <Sparkles className={iconSize} />
            </button>
          )}

          {/* Send button */}
          <button
            type="button"
            onClick={() => void handleSend()}
            disabled={!canSend}
            title="Send (Ctrl+Enter)"
            className={`
              flex items-center justify-center rounded-lg bg-navy-600 text-white
              hover:bg-navy-700 active:bg-navy-800
              transition-colors duration-200
              disabled:opacity-50 disabled:cursor-not-allowed ${iconBtn}
            `}
          >
            {isSending ? (
              <Loader2 className={`animate-spin ${iconSize}`} />
            ) : (
              <Send className={iconSize} />
            )}
          </button>
        </div>
      </div>

      {/* Status / hint line */}
      {!compact && (
        <div className="mt-1 flex items-center gap-2 text-xs min-h-[16px]">
          {voice.state === 'error' && voice.error ? (
            <span className="text-red-500">{voice.error}</span>
          ) : polish.state === 'error' && polish.error ? (
            <span className="text-red-500">{polish.error}</span>
          ) : isRecording ? (
            <span className="text-red-500">Recording… {voice.recordingDuration}s — tap to stop</span>
          ) : isTranscribing ? (
            <span className="text-slate-400">Transcribing…</span>
          ) : isPolishing ? (
            <span className="text-slate-400">Polishing…</span>
          ) : text.length > 0 ? (
            <span className="text-slate-400">Press Ctrl+Enter to send</span>
          ) : null}
        </div>
      )}
    </div>
  );
}
